/**
 * Catalogue picker data layer (NOVA-011/032) — the ONE shared place both the reception BasketBuilder and
 * the admin PricingPage talk to the catalogue search surface. Every path + capability below is
 * code-verified against server/src/modules/catalogue/{routes,controller,service}.js (mounted under /api/v1).
 *
 * Two projections, ONE read capability (`catalogue.parametre.read`):
 *   GET /catalogue/parametres?q=        central code list (code + libellé + discipline; statut ≠ retire)
 *   GET /catalogue/parametres-labo?q=   the lab's PRICED overlays (parametre_labo_id + libellé + prix)
 *
 * The two screens need DIFFERENT shapes: PricingPage picks a central CODE to instantiate/ledger a price;
 * BasketBuilder needs the lab overlay id (an un-priced central code is NOT addable). Both behind the same
 * read cap. Server data lives only in TanStack Query — never copied into a store.
 */
import { api, qk, useApiQuery } from "@/api";
import { useAuthStore } from "@/auth";
import type { ParametreHit, ParametreLaboHit } from "./types";

/** Lab id for query scoping — from the live auth session (multi-tenant cache isolation). */
function useLabId(): string | null {
  return useAuthStore((s) => s.user?.laboratoireId ?? s.lab?.id ?? null);
}

/** Lab-scoped query keys for the catalogue picker. */
export const catalogueKeys = {
  parametres: (labId: string | null, q: string) => qk.scoped(labId, "catalogue", "parametres", q),
  parametresLabo: (labId: string | null, q: string) => qk.scoped(labId, "catalogue", "parametres-labo", q),
};

/**
 * Central-code typeahead (PricingPage). Disabled until `q` is non-empty so the picker only queries on a
 * real search term (the full list is already served by the page's own list call). Gated server-side by
 * `catalogue.parametre.read`; the caller also gates the UI on it.
 */
export function useParametreSearch(q: string, enabled = true) {
  const labId = useLabId();
  const trimmed = q.trim();
  return useApiQuery<ParametreHit[]>(
    catalogueKeys.parametres(labId, trimmed),
    "/catalogue/parametres",
    { query: { q: trimmed }, enabled: enabled && trimmed.length >= 1, staleTime: 30_000 },
  );
}

/**
 * Lab-overlay typeahead (reception BasketBuilder). Returns the addable PRICED overlays (parametre_labo_id
 * + prix). Disabled until `q` is non-empty. Gated server-side by `catalogue.parametre.read`.
 */
export function useParametreLaboSearch(q: string, enabled = true) {
  const labId = useLabId();
  const trimmed = q.trim();
  return useApiQuery<ParametreLaboHit[]>(
    catalogueKeys.parametresLabo(labId, trimmed),
    "/catalogue/parametres-labo",
    { query: { q: trimmed }, enabled: enabled && trimmed.length >= 1, staleTime: 30_000 },
  );
}

/** Imperative central-code search (rare — prefer the hook). */
export function searchParametres(q: string): Promise<ParametreHit[]> {
  return api.get<ParametreHit[]>("/catalogue/parametres", { q });
}
